<template>
  <div>
    <div v-if="label" class="tw-mb-1 tw-text-mb-body lg:tw-text-dt-body">
      <span :class="hasError ? 'tw-text-error' : 'tw-text-black'">
        {{ label }}
      </span>
      <span v-if="isRequired" class="tw-text-[#DC3823]">*</span>
    </div>
    <QInput
      ref="input"
      v-model="innerValue"
      :class="containerClass"
      :data-cy="dataCy"
      :placeholder="placeholder"
      :mask="mask"
      :type="type"
      :fill-mask="fillMask"
      :reverse-fill-mask="reverseFillMask"
      :input-class="inputClass"
      :disable="disable"
      :maxlength="maxlength"
      :counter="counter"
      :square="square"
      :outlined="outlined"
      :dense="dense"
      :debounce="debounce"
      :unmasked-value="unmaskedValue"
      autocomplete="off"
      :filled="filled"
      :rules="validationRules"
      :error="hasError"
      :error-message="errorMessage"
      :hide-bottom-space="hideBottomSpace"
    >
      <slot />
    </QInput>
    <div
      v-if="hasError"
      class="tw-body-tiny tw-text-error tw-mt-1"
      :data-cy="`error-input-${dataCy}`"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { parsePhoneNumber } from 'awesome-phonenumber'

const props = withDefaults(defineProps<{
  modelValue?: string | number
  name?: string
  rules?: string
  label?: string
  dataCy?: string
  placeholder?: string
  inputClass?: string
  mask?: string
  unmaskedValue?: boolean
  fillMask?: string | boolean
  reverseFillMask?: boolean
  disable?: boolean
  small?: boolean
  type?: string
  counter?: boolean
  maxlength?: string
  debounce?: string
  square?: boolean
  outlined?: boolean
  dense?: boolean
  filled?: boolean
  containerClass?: string
  hideBottomSpace?: boolean
  errorMessages?: Record<string, string>
}>(), {
  modelValue: '',
  name: '',
  rules: '',
  label: '',
  dataCy: '',
  placeholder: '',
  inputClass: '',
  mask: '',
  unmaskedValue: false,
  fillMask: false,
  reverseFillMask: false,
  disable: false,
  small: false,
  type: 'text',
  counter: false,
  maxlength: '',
  debounce: '',
  square: false,
  outlined: false,
  dense: false,
  filled: true,
  containerClass: '',
  hideBottomSpace: false,
  errorMessages: () => ({}),
})

const input = ref()

const innerValue = defineModel<string | number>({
  default: '',
})

const isRequired = computed(() => props.rules.includes('required'))
const hasError = ref(false)
const errorMessage = ref('')

// Convert vee-validate rules to Quasar rules
const validationRules = computed(() => {
  const rules = []

  if (props.rules.includes('required')) {
    const requiredMsg = props.errorMessages?.required?.replace('{label}', props.label || '') || 'This field is required'
    rules.push((val: string) => !!val || requiredMsg)
  }

  if (props.rules.includes('email')) {
    rules.push((val: string) => {
      const emailPattern = /^(?=[a-zA-Z0-9@._%+-]{6,254}$)[a-zA-Z0-9._%+-]{1,64}@(?:[a-zA-Z0-9-]{1,63}\.){1,8}[a-zA-Z]{2,63}$/
      return !val || emailPattern.test(val) || props.errorMessages?.email || 'Please enter a valid email address'
    })
  }

  if (props.rules.includes('phone')) {
    rules.push((val: string) => {
      if (!val) { return true }
      const regex = /\D/g
      const phoneNumber = val.replace(regex, '') || ''
      const pn = parsePhoneNumber(phoneNumber, { regionCode: 'TH' })
      return pn.valid || props.errorMessages?.phone || 'Please enter a valid phone number'
    })
  }

  return rules
})
</script>
